<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="row mb-3">
          <div
            class="col-lg-2 col-md-4 col-sm-6 mb-2"
            v-for="(stat, idx) in summaryCards"
            :key="idx"
          >
            <div
              class="card mb-0 cursor-pointer"
              :class="{ 'border-primary': filters.status === stat.key }"
              @click="toggleStatusFilter(stat.key)"
            >
              <div class="body text-center py-3">
                <h4 class="mb-0" :class="stat.textClass">{{ stat.count }}</h4>
                <small class="text-muted">{{ stat.label }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Annual Information Review</h2>
          </div>
          <div class="body pt-0">
            <div class="chart-top-action d-lg-flex justify-content-between py-3 fresh-customer-filter">
              <div class="d-flex flex-wrap align-items-center">
                <b-form-select
                  v-model="filters.status"
                  :options="statusOptions"
                  size="sm"
                  class="mr-2 mb-2"
                  style="width: 180px;"
                  @change="applyFilters"
                ></b-form-select>
              </div>
              <div class="d-flex align-items-center">
                <div class="input-group mb-2" style="width: 280px;">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Search name, email, phone, ID..."
                    v-model="filters.search"
                    v-on:keyup.enter="applyFilters"
                  />
                  <div class="input-group-append" @click="applyFilters">
                    <span class="input-group-text"><i class="icon-magnifier"></i></span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-4">
              <b-spinner variant="primary"></b-spinner>
              <div class="text-muted mt-2">Loading annual reviews...</div>
            </div>

            <div v-else-if="storeError" class="alert alert-danger">
              <i class="fa fa-exclamation-circle mr-1"></i> {{ storeError }}
              <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadReviews">
                Retry
              </b-button>
            </div>

            <template v-else>
              <div class="py-1 align-items-center justify-content-end table-pagination">
                <div class="number-page">
                  <b-pagination
                    class="pagination-small"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    first-text="First"
                    last-text="Last"
                    :per-page="perPage"
                    align="right"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </div>

              <b-table
                responsive
                table-class="mb-0 table-custom border"
                head-variant="light"
                :items="reviews"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :no-local-sorting="true"
                @sort-changed="onSortChanged"
                striped
                show-empty
              >
                <template #cell(customer)="row">
                  <div>
                    <a
                      class="font-weight-bold text-primary cursor-pointer"
                      @click="viewReview(row.item)"
                    >
                      {{ customerName(row.item) }}
                    </a>
                    <div class="text-muted small">
                      {{ row.item.customer && row.item.customer.payangel_id }}
                    </div>
                  </div>
                </template>

                <template #cell(status)="row">
                  <span class="badge" :class="statusBadgeClass(row.value)">
                    {{ statusLabel(row.value) }}
                  </span>
                  <span
                    v-if="row.item.awaiting_ce"
                    class="badge badge-warning ml-1"
                  >
                    Awaiting CE
                  </span>
                </template>

                <template #cell(pending_sections)="row">
                  <span
                    v-for="section in row.value"
                    :key="section"
                    class="badge badge-info mr-1 mb-1"
                  >
                    {{ sectionLabel(section) }}
                  </span>
                  <span v-if="!row.value || !row.value.length" class="text-muted">—</span>
                </template>

                <template #cell(updated_at)="row">
                  {{ formatDate(row.value) }}
                </template>

                <template #cell(actions)="row">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="viewReview(row.item)"
                  >
                    <i class="fa fa-eye mr-1"></i> View
                  </b-button>
                </template>

                <template #empty>
                  <div class="text-center text-muted py-4">
                    No annual reviews match the current filters.
                  </div>
                </template>
              </b-table>

              <div class="d-flex py-3 align-items-center justify-content-between table-pagination">
                <div class="per-page">
                  <b-form-group label="Per page" label-for="per-page-select" class="mb-0">
                    <b-form-select
                      id="per-page-select"
                      v-model="perPage"
                      :options="pageOptions"
                      size="sm"
                      @change="onPerPageChange"
                    ></b-form-select>
                  </b-form-group>
                </div>
                <div class="number-page">
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    first-text="First"
                    last-text="Last"
                    :per-page="perPage"
                    align="right"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb.vue";
import { mapGetters } from "vuex";

export default {
  name: "AnnualReviewsQueue",
  components: { BreadCrumb },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Members", links: "/customer/list" },
        { title: "Annual Review", links: "" },
      ],
      filters: {
        status: "awaiting_ce",
        search: "",
      },
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      sortBy: "updated_at",
      sortDesc: true,
      fields: [
        { key: "customer", label: "Remitter", sortable: false },
        { key: "cycle_no", label: "Cycle", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "pending_sections", label: "Pending sections", sortable: false },
        { key: "updated_at", label: "Updated", sortable: true },
        { key: "actions", label: "Actions", sortable: false },
      ],
      statusOptions: [
        { value: "awaiting_ce", text: "Awaiting CE" },
        { value: "in_progress", text: "In progress" },
        { value: "pending", text: "Pending" },
        { value: "completed", text: "Completed" },
        { value: "all", text: "All" },
      ],
    };
  },
  computed: {
    ...mapGetters("annualReview", [
      "annualReviews",
      "annualReviewSummary",
      "annualReviewPagination",
      "annualReviewLoading",
      "annualReviewError",
    ]),
    reviews() {
      return this.annualReviews || [];
    },
    isLoading() {
      return this.annualReviewLoading;
    },
    storeError() {
      return this.annualReviewError;
    },
    totalRows() {
      return this.annualReviewPagination?.total_items || 0;
    },
    summaryCards() {
      const s = this.annualReviewSummary || {};
      return [
        { key: "awaiting_ce", label: "Awaiting CE", count: s.awaiting_ce || 0, textClass: "text-warning" },
        { key: "in_progress", label: "In progress", count: s.in_progress || 0, textClass: "text-primary" },
        { key: "pending", label: "Pending", count: s.pending || 0, textClass: "text-muted" },
        { key: "completed", label: "Completed", count: s.completed || 0, textClass: "text-success" },
      ];
    },
  },
  mounted() {
    this.loadReviews();
  },
  methods: {
    loadReviews() {
      this.$store.dispatch("annualReview/fetchAnnualReviews", {
        page: this.currentPage,
        limit: this.perPage,
        status: this.filters.status,
        search: this.filters.search || undefined,
        sort: this.sortBy,
        order: this.sortDesc ? "DESC" : "ASC",
      });
    },
    applyFilters() {
      this.currentPage = 1;
      this.loadReviews();
    },
    toggleStatusFilter(key) {
      this.filters.status = this.filters.status === key ? "all" : key;
      this.applyFilters();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadReviews();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.loadReviews();
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy || "updated_at";
      this.sortDesc = !!ctx.sortDesc;
      this.loadReviews();
    },
    viewReview(item) {
      this.$router.push({
        name: "annual-review-detail",
        params: { review_id: item.id },
      });
    },
    customerName(item) {
      const c = item.customer || {};
      return [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
    },
    sectionLabel(key) {
      const map = {
        personal_details: "Personal",
        home_address: "Address",
        contact_details: "Contact",
        occupation: "Occupation",
      };
      return map[key] || key;
    },
    statusLabel(status) {
      const map = {
        pending: "Pending",
        in_progress: "In progress",
        completed: "Completed",
        overdue: "Overdue",
      };
      return map[status] || status;
    },
    statusBadgeClass(status) {
      const map = {
        pending: "badge-secondary",
        in_progress: "badge-primary",
        completed: "badge-success",
        overdue: "badge-danger",
      };
      return map[status] || "badge-secondary";
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },
  },
};
</script>
